// ============================================================================
// DINEOPS SERVICE
// ============================================================================
// Calls for the DineOps restaurant module.
//
// NO CALL PASSES A dineOutletId. The API resolves the outlet from the request's
// identity and refuses to take one as a parameter, so that a caller cannot read
// another outlet's card by editing a query string. There is deliberately nothing
// here to pass it through.

import axiosClient, { get, post, put, del } from '../client/axiosClient';
import { getActiveDineOutletId } from '../../auth/dineOutlet';
import { buildEndpoint, buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  PaginatedResponse,
  ID,
  DineOutlet,
  DineOpsHealth,
  DineCourse,
  DineMenuGroup,
  DineItem,
  DineItemSearchRequest,
  DineQueryRequest,
  CreateDineItemRequest,
  UpdateDineItemRequest,
  CreateDineCourseRequest,
  UpdateDineCourseRequest,
  CreateDineMenuGroupRequest,
  UpdateDineMenuGroupRequest,
  DineTagGroup,
  DineTagAnswer,
  TagAnswerInput,
  DineAddonGroup,
  DineResolvedAddons,
  DineItemVariant,
  DineItemPhoto,
  UpdateItemPhotoInput,
  DineTaxRate,
  DineItemTaxHistory,
  DinePair,
  DineItemPairs,
  PairInput,
  DineBoard,
  DineBillSummary,
  DineBillDetail,
  DineAwaitingBill,
  DineBillPrint,
  DineSalesReport,
  DineSalesCategoryReport,
  DineOutletItem,
  DineItemOutlet,
  DineOutletItemQuery,
  PriceBoardQuery,
  PriceBoardOutlet,
  PriceBoardRow,
  SavePriceBoardRequest,
  SavePriceBoardResult,
  PriceBoardImportResult,
  AddDineOutletItemsRequest,
  AddDineOutletItemsResult,
  UpdateDineOutletItemRequest,
  DineOutletStation,
  DineOutletMealPeriod,
  SaveDineOutletMealPeriod,
  DineOutletTaxProfiles,
  SaveDineOutletTaxProfile,
  CompInput,
  DineOutletBuffetSettings,
  DineBillingPoint,
  SaveDineBillingPointRequest,
  DineServableOutlet,
  DineBillRegister,
  DineApprovalRule,
  SaveDineApprovalRuleRequest,
  DineSettlementReport,
  DineWastageReport,
  DineComplaintsReport,
  DineKitchenReport,
  DineDashboard,
  DineRevenueSummary,
  DineShift,
  DineCorrection,
  DineDayClose,
  DineComplaint,
  RefundInput,
  ReissueInput,
  ReviewInput,
  ComplaintInput,
  DiscountInput,
  BillCustomerInput,
  PaymentInput,
  DinePriceList,
  ItemPriceInput,
  CreatePriceListRequest,
  UpdatePriceListRequest,
  NewPriceListVersionRequest,
  DineKot,
  DineKotPrint,
  CreateDineSpecialRequest,
  BulkDineSpecialRequest,
  UpdateDineSpecialRequest,
  CreateDineTagGroupRequest,
  DineTag,
  UpdateDineTagGroupRequest,
  DineTagInput,
  VariantInput,
  ItemAddonInput,
  DineSection,
  DineDiningTable,
  DineKitchenStation,
  DineKitchen,
  SaveKitchenRequest,
  KitchenListParams,
  DineRequisition,
  SaveRequisitionRequest,
  RequisitionListParams,
  RequisitionItemOption,
  RequisitionRegister,
  DineRequisitionPurpose,
  SaveRequisitionPurposeRequest,
  DineOutletBrief,
  DineOutletAdmin,
  CreateDineOutletRequest,
  UpdateDineOutletRequest,
  RoleOutletAccess,
  RoomChargeCheck,
  RoleStationAccess,
  SaveRoleStationAccessRequest,
  MyStationAccess,
  RoleOutletUser,
  SaveRoleOutletAccessRequest,
  DineQuickNote,
  DineKotPrintStatus,
  StationChannelPrinter,
} from '../types';

/**
 * A department's printing (KOT printing, D-P10). No primaryPrinterId = kitchen
 * screen and browser print. channelPrinters is the FULL list; leaving it out on
 * update keeps what is saved.
 */
type StationPrintingInput = {
  primaryPrinterId?: string;
  backupPrinterId?: string;
  printCopies?: number;
  channelPrinters?: Pick<StationChannelPrinter, 'orderChannel' | 'printerId'>[];
};

/**
 * Everything DineOps sits under one prefix.
 *
 * EVERY path goes through buildEndpoint, which prepends the API version. The
 * axios base URL ends at `/api`, so a raw `/dine/...` resolves to
 * `/api/dine/...` and 404s — the version segment is the caller's to add.
 *
 * That is not hypothetical: the nine list calls used buildEndpointWithQuery and
 * worked, while the other thirty-seven were written as bare templates and every
 * create, update and delete 404'd. The screens rendered perfectly, because
 * reading was the part that happened to be correct.
 */
const BASE = '/dine';

/**
 * Unwraps the standard envelope's paged payload.
 * The API returns { success, message, data: { items, page, ... } }.
 */
function toPaged<T>(raw: any): PaginatedResponse<T> {
  const paged = raw?.data ?? {};
  return {
    success: raw?.success,
    message: raw?.message,
    data: paged.items ?? [],
    pagination: {
      currentPage: paged.page ?? 1,
      pageSize: paged.pageSize ?? 25,
      totalPages: paged.totalPages ?? 0,
      totalItems: paged.totalCount ?? 0,
      hasNextPage: paged.hasNext ?? false,
      hasPreviousPage: paged.hasPrevious ?? false,
    },
  };
}

class DineOpsService {
  // ------------------------------------------------------------ the outlet

  /** Anonymous by design: you need it most when sign-in is what is failing. */
  async getHealth(): Promise<DineOpsHealth> {
    const res = await get<any>(buildEndpoint(`${BASE}/health`));
    return res.data?.data;
  }

  async getOutlet(): Promise<DineOutlet> {
    const res = await get<any>(buildEndpoint(`${BASE}/outlet`));
    return res.data?.data;
  }

  // -------------------------------------------------------------- courses

  async getCourses(params?: DineQueryRequest): Promise<PaginatedResponse<DineCourse>> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/menu/courses`, params ?? {}));
    return toPaged<DineCourse>(res.data);
  }

  async createCourse(body: CreateDineCourseRequest): Promise<DineCourse> {
    const res = await post<any>(buildEndpoint(`${BASE}/menu/courses`), body);
    return res.data?.data;
  }

  async updateCourse(id: ID, body: UpdateDineCourseRequest): Promise<DineCourse> {
    const res = await put<any>(buildEndpoint(`${BASE}/menu/courses/${id}`), body);
    return res.data?.data;
  }

  /** rowVersion goes in the query string: a DELETE has no body to carry it. */
  async deleteCourse(id: ID, rowVersion: number): Promise<void> {
    await del<any>(buildEndpoint(`${BASE}/menu/courses/${id}?rowVersion=${rowVersion}`));
  }

  // ---------------------------------------------------------- menu groups

  async getGroups(params?: DineQueryRequest): Promise<PaginatedResponse<DineMenuGroup>> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/menu/groups`, params ?? {}));
    return toPaged<DineMenuGroup>(res.data);
  }

  async createGroup(body: CreateDineMenuGroupRequest): Promise<DineMenuGroup> {
    const res = await post<any>(buildEndpoint(`${BASE}/menu/groups`), body);
    return res.data?.data;
  }

  async updateGroup(id: ID, body: UpdateDineMenuGroupRequest): Promise<DineMenuGroup> {
    const res = await put<any>(buildEndpoint(`${BASE}/menu/groups/${id}`), body);
    return res.data?.data;
  }

  async deleteGroup(id: ID, rowVersion: number): Promise<void> {
    await del<any>(buildEndpoint(`${BASE}/menu/groups/${id}?rowVersion=${rowVersion}`));
  }

  async getGroupTags(id: ID): Promise<DineTagAnswer[]> {
    const res = await get<any>(buildEndpoint(`${BASE}/menu/groups/${id}/tags`));
    return res.data?.data ?? [];
  }

  async saveGroupTags(id: ID, answers: TagAnswerInput[]): Promise<DineTagAnswer[]> {
    const res = await put<any>(buildEndpoint(`${BASE}/menu/groups/${id}/tags`), { answers });
    return res.data?.data ?? [];
  }

  // ---------------------------------------------------------------- items

  async getItems(params?: DineItemSearchRequest): Promise<PaginatedResponse<DineItem>> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/menu/items`, params ?? {}));
    return toPaged<DineItem>(res.data);
  }

  async getItem(id: ID): Promise<DineItem> {
    const res = await get<any>(buildEndpoint(`${BASE}/menu/items/${id}`));
    return res.data?.data;
  }

  /** The waiter's picker. Capped server side; fires on every keystroke. */
  async searchItems(q: string, limit = 20): Promise<DineItem[]> {
    const res = await get<any>(buildEndpoint(`${BASE}/menu/items/search?q=${encodeURIComponent(q)}&limit=${limit}`));
    return res.data?.data ?? [];
  }

  async createItem(body: CreateDineItemRequest): Promise<DineItem> {
    const res = await post<any>(buildEndpoint(`${BASE}/menu/items`), body);
    return res.data?.data;
  }

  async updateItem(id: ID, body: UpdateDineItemRequest): Promise<DineItem> {
    const res = await put<any>(buildEndpoint(`${BASE}/menu/items/${id}`), body);
    return res.data?.data;
  }

  async deleteItem(id: ID, rowVersion: number): Promise<void> {
    await del<any>(buildEndpoint(`${BASE}/menu/items/${id}?rowVersion=${rowVersion}`));
  }

  // ----------------------------------------------------------------- tags

  async getTagGroups(params?: DineQueryRequest): Promise<PaginatedResponse<DineTagGroup>> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/menu/tag-groups`, params ?? {}));
    return toPaged<DineTagGroup>(res.data);
  }

  /** The Attributes tab: every group, with the dish's resolved answer. */
  async getTagGroup(id: ID): Promise<DineTagGroup> {
    const res = await get<any>(buildEndpoint(`${BASE}/menu/tag-groups/${id}`));
    return res.data?.data;
  }

  async createTagGroup(body: CreateDineTagGroupRequest): Promise<DineTagGroup> {
    const res = await post<any>(buildEndpoint(`${BASE}/menu/tag-groups`), body);
    return res.data?.data;
  }

  async updateTagGroup(id: ID, body: UpdateDineTagGroupRequest): Promise<DineTagGroup> {
    const res = await put<any>(buildEndpoint(`${BASE}/menu/tag-groups/${id}`), body);
    return res.data?.data;
  }

  /** Refused (409) for a bound group: its column needs it. */
  async deleteTagGroup(id: ID, rowVersion: number): Promise<void> {
    await del<any>(buildEndpoint(`${BASE}/menu/tag-groups/${id}?rowVersion=${rowVersion}`));
  }

  /** Replaces the group's whole tag set. A tag left out is removed. */
  async saveTags(id: ID, tags: DineTagInput[]): Promise<DineTag[]> {
    const res = await put<any>(buildEndpoint(`${BASE}/menu/tag-groups/${id}/tags`), { tags });
    return res.data?.data ?? [];
  }

  async getItemTags(id: ID): Promise<DineTagAnswer[]> {
    const res = await get<any>(buildEndpoint(`${BASE}/menu/items/${id}/tags`));
    return res.data?.data ?? [];
  }

  /** Saves the answers and the four bound columns in one transaction. */
  async saveItemTags(id: ID, answers: TagAnswerInput[]): Promise<DineTagAnswer[]> {
    const res = await put<any>(buildEndpoint(`${BASE}/menu/items/${id}/tags`), { answers });
    return res.data?.data ?? [];
  }

  // ------------------------------------------------- add-ons and variants

  async getAddonGroups(params?: DineQueryRequest): Promise<PaginatedResponse<DineAddonGroup>> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/menu/addon-groups`, params ?? {}));
    return toPaged<DineAddonGroup>(res.data);
  }

  async createAddonGroup(body: { addonGroupName: string; description?: string }): Promise<DineAddonGroup> {
    const res = await post<any>(buildEndpoint(`${BASE}/menu/addon-groups`), body);
    return res.data?.data;
  }

  async updateAddonGroup(
    id: ID,
    body: { addonGroupName: string; description?: string; isActive: boolean; rowVersion: number },
  ): Promise<DineAddonGroup> {
    const res = await put<any>(buildEndpoint(`${BASE}/menu/addon-groups/${id}`), body);
    return res.data?.data;
  }

  async deleteAddonGroup(id: ID, rowVersion: number): Promise<void> {
    await del<any>(buildEndpoint(`${BASE}/menu/addon-groups/${id}?rowVersion=${rowVersion}`));
  }

  async saveAddons(
    addonGroupId: ID,
    addons: { id?: ID; addonItemId: ID; displayOrder: number }[],
  ): Promise<any[]> {
    const res = await put<any>(buildEndpoint(`${BASE}/menu/addon-groups/${addonGroupId}/addons`), { addons });
    return res.data?.data ?? [];
  }

  async getItemAddons(id: ID): Promise<DineResolvedAddons> {
    const res = await get<any>(buildEndpoint(`${BASE}/menu/items/${id}/addons`));
    return res.data?.data;
  }

  /** An empty list puts the dish back to following its menu group's set. */
  async saveItemAddons(id: ID, addons: ItemAddonInput[]): Promise<DineResolvedAddons> {
    const res = await put<any>(buildEndpoint(`${BASE}/menu/items/${id}/addons`), { addons });
    return res.data?.data;
  }

  async getVariants(id: ID): Promise<DineItemVariant[]> {
    const res = await get<any>(buildEndpoint(`${BASE}/menu/items/${id}/variants`));
    return res.data?.data ?? [];
  }

  /** None, or at least two with exactly one default. */
  async saveVariants(id: ID, variants: VariantInput[]): Promise<DineItemVariant[]> {
    const res = await put<any>(buildEndpoint(`${BASE}/menu/items/${id}/variants`), { variants });
    return res.data?.data ?? [];
  }

  // --------------------------------------------------- floor and kitchen

  async getSections(params?: DineQueryRequest): Promise<PaginatedResponse<DineSection>> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/sections`, params ?? {}));
    return toPaged<DineSection>(res.data);
  }

  async createSection(body: { sectionName: string; displayOrder: number; billingPointId?: string }): Promise<DineSection> {
    const res = await post<any>(buildEndpoint(`${BASE}/sections`), body);
    return res.data?.data;
  }

  async updateSection(
    id: ID,
    body: { sectionName: string; displayOrder: number; billingPointId?: string; isActive: boolean; rowVersion: number },
  ): Promise<DineSection> {
    const res = await put<any>(buildEndpoint(`${BASE}/sections/${id}`), body);
    return res.data?.data;
  }

  async deleteSection(id: ID, rowVersion: number): Promise<void> {
    await del<any>(buildEndpoint(`${BASE}/sections/${id}?rowVersion=${rowVersion}`));
  }

  /** The table board: each table with its derived live state, in one call. */
  async getTables(params?: DineQueryRequest): Promise<PaginatedResponse<DineDiningTable>> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/tables`, params ?? {}));
    return toPaged<DineDiningTable>(res.data);
  }

  /** Seats are optional: left out, the API gives the table its default seat count. */
  async createTable(body: { tableNumber: string; capacity?: number; sectionId?: ID }): Promise<DineDiningTable> {
    const res = await post<any>(buildEndpoint(`${BASE}/tables`), body);
    return res.data?.data;
  }

  /** Many tables at once (e.g. T01 to T35). Numbers the outlet already has come back in `skipped`. */
  async bulkCreateTables(body: { tableNumbers: string[]; sectionId?: ID }): Promise<{ created: string[]; skipped: string[] }> {
    const res = await post<any>(buildEndpoint(`${BASE}/tables/bulk`), body);
    return res.data?.data ?? { created: [], skipped: [] };
  }

  /** Seats left out (or 0) keep the table's current count. */
  async updateTable(
    id: ID,
    body: { tableNumber: string; capacity?: number; sectionId?: ID; isActive: boolean; rowVersion: number },
  ): Promise<DineDiningTable> {
    const res = await put<any>(buildEndpoint(`${BASE}/tables/${id}`), body);
    return res.data?.data;
  }

  async deleteTable(id: ID, rowVersion: number): Promise<void> {
    await del<any>(buildEndpoint(`${BASE}/tables/${id}?rowVersion=${rowVersion}`));
  }

  // ------------------------------------------------------------ outlets (O9)

  /** The outlets the signed-in user may work in: the header switcher and the waiter app sign-in. */
  async getMyOutlets(): Promise<DineOutletBrief[]> {
    const res = await get<any>(buildEndpoint(`${BASE}/outlets/mine`));
    return res.data?.data ?? [];
  }

  async getOutlets(includeInactive = false): Promise<DineOutletAdmin[]> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/outlets`, { includeInactive }));
    return res.data?.data ?? [];
  }

  async createOutlet(body: CreateDineOutletRequest): Promise<DineOutletAdmin> {
    const res = await post<any>(buildEndpoint(`${BASE}/outlets`), body);
    return res.data?.data;
  }

  async updateOutlet(id: ID, body: UpdateDineOutletRequest): Promise<DineOutletAdmin> {
    const res = await put<any>(buildEndpoint(`${BASE}/outlets/${id}`), body);
    return res.data?.data;
  }

  /** A role's outlet access (Role Permissions > Restaurant tab); PER_USER when nothing is saved yet. */
  async getRoleOutletAccess(roleCode: string): Promise<RoleOutletAccess> {
    const res = await get<any>(buildEndpoint(`${BASE}/role-outlet-access/roles/${encodeURIComponent(roleCode)}`));
    return res.data?.data;
  }

  /** Saves the role's mode, its outlets (SELECTED) and the changed users' own outlets (PER_USER). */
  async saveRoleOutletAccess(roleCode: string, body: SaveRoleOutletAccessRequest): Promise<RoleOutletAccess> {
    const res = await put<any>(buildEndpoint(`${BASE}/role-outlet-access/roles/${encodeURIComponent(roleCode)}`), body);
    return res.data?.data;
  }

  /** A role's kitchen departments (Role Permissions > Restaurant tab); ALL when nothing is saved. */
  async getRoleStationAccess(roleCode: string): Promise<RoleStationAccess> {
    const res = await get<any>(buildEndpoint(`${BASE}/role-station-access/roles/${encodeURIComponent(roleCode)}`));
    return res.data?.data;
  }

  async saveRoleStationAccess(roleCode: string, body: SaveRoleStationAccessRequest): Promise<RoleStationAccess> {
    const res = await put<any>(buildEndpoint(`${BASE}/role-station-access/roles/${encodeURIComponent(roleCode)}`), body);
    return res.data?.data;
  }

  /** The departments the signed-in user may work: what the kitchen display shows. */
  async getMyStations(): Promise<MyStationAccess> {
    const res = await get<any>(buildEndpoint(`${BASE}/role-station-access/mine`));
    return res.data?.data ?? { all: true, stationIds: [] };
  }

  /** Every user of the tenant (Tenant Hub and CRS), with their CRS role and own outlets. */
  async getRoleOutletUsers(): Promise<RoleOutletUser[]> {
    const res = await get<any>(buildEndpoint(`${BASE}/role-outlet-access/users`));
    return res.data?.data ?? [];
  }

  /** Every department, and whether this outlet may send to it (departments are shared, DO-312). */
  async getOutletStations(id: ID): Promise<DineOutletStation[]> {
    const res = await get<any>(buildEndpoint(`${BASE}/outlets/${id}/stations`));
    return res.data?.data ?? [];
  }

  /** Replaces the departments the outlet may send to; refused while a dish sold there still goes to one removed. */
  async replaceOutletStations(id: ID, stationIds: string[]): Promise<DineOutletStation[]> {
    const res = await put<any>(buildEndpoint(`${BASE}/outlets/${id}/stations`), { stationIds });
    return res.data?.data ?? [];
  }

  // ----------------------------------------------- tax profile (DineOps tax T-1)

  /** The outlet's tax profile history, the rate codes to choose from, and whether the user may add a row. */
  async getOutletTaxProfiles(id: ID): Promise<DineOutletTaxProfiles> {
    const res = await get<any>(buildEndpoint(`${BASE}/outlets/${id}/tax-profiles`));
    return res.data?.data;
  }

  /** A new profile from a date; the current one ends the day before. */
  async addOutletTaxProfile(id: ID, body: SaveDineOutletTaxProfile): Promise<DineOutletTaxProfiles> {
    const res = await post<any>(buildEndpoint(`${BASE}/outlets/${id}/tax-profiles`), body);
    return res.data?.data;
  }

  // ----------------------------------------------- meal periods (buffet slice 2)

  /** The meals this outlet serves and how: buffet, à la carte or not served. */
  async getOutletMealPeriods(id: ID): Promise<DineOutletMealPeriod[]> {
    const res = await get<any>(buildEndpoint(`${BASE}/outlets/${id}/meal-periods`));
    return res.data?.data ?? [];
  }

  async createOutletMealPeriod(id: ID, body: SaveDineOutletMealPeriod): Promise<DineOutletMealPeriod> {
    const res = await post<any>(buildEndpoint(`${BASE}/outlets/${id}/meal-periods`), body);
    return res.data?.data;
  }

  async updateOutletMealPeriod(id: ID, periodId: ID, body: SaveDineOutletMealPeriod): Promise<DineOutletMealPeriod> {
    const res = await put<any>(buildEndpoint(`${BASE}/outlets/${id}/meal-periods/${periodId}`), body);
    return res.data?.data;
  }

  async deleteOutletMealPeriod(id: ID, periodId: ID, rowVersion: number): Promise<void> {
    await del<any>(buildEndpoint(`${BASE}/outlets/${id}/meal-periods/${periodId}?rowVersion=${rowVersion}`));
  }

  /** The age under which children eat a paid buffet free (buffet slice 3). */
  async getOutletBuffetSettings(id: ID): Promise<DineOutletBuffetSettings> {
    const res = await get<any>(buildEndpoint(`${BASE}/outlets/${id}/meal-periods/settings`));
    return res.data?.data ?? { childFreeAge: 5 };
  }

  async saveOutletBuffetSettings(id: ID, body: DineOutletBuffetSettings): Promise<DineOutletBuffetSettings> {
    const res = await put<any>(buildEndpoint(`${BASE}/outlets/${id}/meal-periods/settings`), body);
    return res.data?.data;
  }

  // ----------------------------------------------- outlet items (DO-312, header outlet)

  /** Master dishes with the header outlet's listing, paged on the server. */
  async getOutletItems(params?: DineOutletItemQuery): Promise<PaginatedResponse<DineOutletItem>> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/outlet-items`, params ?? {}));
    return toPaged<DineOutletItem>(res.data);
  }

  /** The outlets the user may work in that sell one dish (the dish window's Sold in). */
  async getItemOutlets(itemId: ID): Promise<DineItemOutlet[]> {
    const res = await get<any>(buildEndpoint(`${BASE}/outlet-items/by-item/${itemId}`));
    return res.data?.data ?? [];
  }

  /** Start selling dishes in the header outlet: named dishes, a group or a course. */
  async addOutletItems(body: AddDineOutletItemsRequest): Promise<AddDineOutletItemsResult> {
    const res = await post<any>(buildEndpoint(`${BASE}/outlet-items`), body);
    return res.data?.data;
  }

  async updateOutletItem(id: ID, body: UpdateDineOutletItemRequest): Promise<DineOutletItem> {
    const res = await put<any>(buildEndpoint(`${BASE}/outlet-items/${id}`), body);
    return res.data?.data;
  }

  /** Stop selling the dish in the header outlet; the master dish stays. */
  async removeOutletItem(id: ID, rowVersion: number): Promise<void> {
    await del<any>(buildEndpoint(`${BASE}/outlet-items/${id}?rowVersion=${rowVersion}`));
  }

  // ----------------------------------------------- Price Board (item pricing P1; every outlet you work in)

  /** The board's outlet columns: every outlet the user may work in. */
  async getPriceBoardOutlets(): Promise<PriceBoardOutlet[]> {
    const res = await get<any>(buildEndpoint(`${BASE}/price-board/outlets`));
    return res.data?.data ?? [];
  }

  /** One page of dishes (sizes under their dish). totalItems counts dishes. */
  async getPriceBoard(params: PriceBoardQuery): Promise<PaginatedResponse<PriceBoardRow>> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/price-board`, params));
    return toPaged<PriceBoardRow>(res.data);
  }

  /** Saves the changed cells with one reason; cells changed by someone else come back in notSaved. */
  async savePriceBoard(body: SavePriceBoardRequest): Promise<SavePriceBoardResult> {
    const res = await put<any>(buildEndpoint(`${BASE}/price-board`), body);
    return res.data?.data;
  }

  /** The board (current filters, every page) as an Excel workbook. */
  async exportPriceBoard(params: PriceBoardQuery): Promise<{ blob: Blob; disposition?: string }> {
    const r = await get<Blob>(buildEndpointWithQuery(`${BASE}/price-board/export`, params), { responseType: 'blob' });
    return { blob: r.data, disposition: r.headers?.['content-disposition'] as string | undefined };
  }

  /** Reads an exported workbook and returns the changes it asks for. Nothing is saved. */
  async importPriceBoard(file: File): Promise<PriceBoardImportResult> {
    const form = new FormData();
    form.append('file', file, file.name);
    const res = await post<any>(buildEndpoint(`${BASE}/price-board/import`), form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data?.data;
  }

  /** Kitchen stations, a tenant master: with propertyId only that property's (used by its outlets, in its kitchens, or unclaimed). */
  async getStations(params?: DineQueryRequest & { propertyId?: string; kitchenId?: string }): Promise<PaginatedResponse<DineKitchenStation>> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/stations`, params ?? {}));
    return toPaged<DineKitchenStation>(res.data);
  }

  async createStation(body: { stationCode: string; stationName: string; printerName?: string; fallbackStationId?: string; kitchenId?: string } & StationPrintingInput): Promise<DineKitchenStation> {
    const res = await post<any>(buildEndpoint(`${BASE}/stations`), body);
    return res.data?.data;
  }

  async updateStation(
    id: ID,
    body: { stationCode: string; stationName: string; printerName?: string; fallbackStationId?: string; kitchenId: string | null; isActive: boolean; rowVersion: number } & StationPrintingInput,
  ): Promise<DineKitchenStation> {
    const res = await put<any>(buildEndpoint(`${BASE}/stations/${id}`), body);
    return res.data?.data;
  }

  /** Closes a station; its fallback takes its dishes only when fallbackOn (spec 8.3b). */
  async closeStation(id: ID, fallbackOn: boolean): Promise<DineKitchenStation> {
    const res = await post<any>(buildEndpoint(`${BASE}/stations/${id}/close`), { fallbackOn });
    return res.data?.data;
  }

  async reopenStation(id: ID): Promise<DineKitchenStation> {
    const res = await post<any>(buildEndpoint(`${BASE}/stations/${id}/reopen`), {});
    return res.data?.data;
  }

  /** Stops or restarts a closed station's fallback. */
  async setStationFallback(id: ID, on: boolean): Promise<DineKitchenStation> {
    const res = await post<any>(buildEndpoint(`${BASE}/stations/${id}/fallback`), { on });
    return res.data?.data;
  }

  async deleteStation(id: ID, rowVersion: number): Promise<void> {
    await del<any>(buildEndpoint(`${BASE}/stations/${id}?rowVersion=${rowVersion}`));
  }

  // ---------------------------------------------------------------- kitchens

  /** The Kitchens screen: one page, searched and filtered on the server. */
  async getKitchens(params: KitchenListParams): Promise<PaginatedResponse<DineKitchen>> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/kitchens`, params));
    return toPaged<DineKitchen>(res.data);
  }

  /** For pickers: a property's kitchens and the shared ones; every kitchen without a property. */
  async getKitchenLookup(propertyId?: string, includeInactive = false): Promise<DineKitchen[]> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/kitchens/lookup`, { propertyId, includeInactive }));
    return res.data?.data ?? [];
  }

  /** One kitchen with its stations. */
  async getKitchen(id: ID): Promise<DineKitchen> {
    const res = await get<any>(buildEndpoint(`${BASE}/kitchens/${id}`));
    return res.data?.data;
  }

  async createKitchen(body: SaveKitchenRequest): Promise<DineKitchen> {
    const res = await post<any>(buildEndpoint(`${BASE}/kitchens`), body);
    return res.data?.data;
  }

  async updateKitchen(id: ID, body: SaveKitchenRequest): Promise<DineKitchen> {
    const res = await put<any>(buildEndpoint(`${BASE}/kitchens/${id}`), body);
    return res.data?.data;
  }

  /** Refused while stations are in the kitchen. */
  async deleteKitchen(id: ID, rowVersion: number): Promise<void> {
    await del<any>(buildEndpoint(`${BASE}/kitchens/${id}?rowVersion=${rowVersion}`));
  }

  // ---- Requisitions (R1): send and issue act in the header outlet, which must be the requisition's.

  async getRequisitions(params: RequisitionListParams): Promise<PaginatedResponse<DineRequisition>> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/requisitions`, params));
    return toPaged<DineRequisition>(res.data);
  }

  async getRequisition(id: ID): Promise<DineRequisition> {
    const res = await get<any>(buildEndpoint(`${BASE}/requisitions/${id}`));
    return res.data?.data;
  }

  async getRequisitionItems(dineOutletId: ID): Promise<RequisitionItemOption[]> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/requisitions/items`, { dineOutletId }));
    return res.data?.data ?? [];
  }

  async createRequisition(body: SaveRequisitionRequest): Promise<DineRequisition> {
    const res = await post<any>(buildEndpoint(`${BASE}/requisitions`), body);
    return res.data?.data;
  }

  async updateRequisition(id: ID, body: SaveRequisitionRequest): Promise<DineRequisition> {
    const res = await put<any>(buildEndpoint(`${BASE}/requisitions/${id}`), body);
    return res.data?.data;
  }

  /** The server's message says what a closed station held back, if anything. */
  async sendRequisition(id: ID): Promise<{ requisition: DineRequisition; message?: string }> {
    const res = await post<any>(buildEndpoint(`${BASE}/requisitions/${id}/send`), {});
    return { requisition: res.data?.data, message: res.data?.message };
  }

  async issueRequisition(id: ID, receivedByName: string, rowVersion: number): Promise<DineRequisition> {
    const res = await post<any>(buildEndpoint(`${BASE}/requisitions/${id}/issue`), { receivedByName, rowVersion });
    return res.data?.data;
  }

  async cancelRequisition(id: ID, reason: string, rowVersion: number): Promise<DineRequisition> {
    const res = await post<any>(buildEndpoint(`${BASE}/requisitions/${id}/cancel`), { reason, rowVersion });
    return res.data?.data;
  }

  /** Approve a requisition awaiting approval (S3): holders of the role it is routed to. */
  async approveRequisition(id: ID, note: string | undefined, rowVersion: number): Promise<DineRequisition> {
    const res = await post<any>(buildEndpoint(`${BASE}/requisitions/${id}/approve`), { note, rowVersion });
    return res.data?.data;
  }

  /** Reject it, with the reason. Final; Copy raises it again. */
  async rejectRequisition(id: ID, reason: string, rowVersion: number): Promise<DineRequisition> {
    const res = await post<any>(buildEndpoint(`${BASE}/requisitions/${id}/reject`), { note: reason, rowVersion });
    return res.data?.data;
  }

  // ------------------------------------------------- approval rules (S3)

  async getApprovalRules(includeInactive = true): Promise<DineApprovalRule[]> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/approval-rules`, { includeInactive }));
    return res.data?.data ?? [];
  }

  async createApprovalRule(body: SaveDineApprovalRuleRequest): Promise<DineApprovalRule> {
    const res = await post<any>(buildEndpoint(`${BASE}/approval-rules`), body);
    return res.data?.data;
  }

  async updateApprovalRule(id: ID, body: SaveDineApprovalRuleRequest): Promise<DineApprovalRule> {
    const res = await put<any>(buildEndpoint(`${BASE}/approval-rules/${id}`), body);
    return res.data?.data;
  }

  async deleteApprovalRule(id: ID, rowVersion: number): Promise<void> {
    await del<any>(buildEndpoint(`${BASE}/approval-rules/${id}?rowVersion=${rowVersion}`));
  }

  /** The tenant's requisition purposes, by display order; inactive ones only when asked. */
  async getRequisitionPurposes(includeInactive = false): Promise<DineRequisitionPurpose[]> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/requisitions/purposes`, { includeInactive }));
    return res.data?.data ?? [];
  }

  async createRequisitionPurpose(body: SaveRequisitionPurposeRequest): Promise<DineRequisitionPurpose> {
    const res = await post<any>(buildEndpoint(`${BASE}/requisitions/purposes`), body);
    return res.data?.data;
  }

  async updateRequisitionPurpose(id: ID, body: SaveRequisitionPurposeRequest): Promise<DineRequisitionPurpose> {
    const res = await put<any>(buildEndpoint(`${BASE}/requisitions/purposes/${id}`), body);
    return res.data?.data;
  }

  async getRequisitionRegister(params: { from: string; to: string; dineOutletId?: string; requestingUnitId?: string; purpose?: string }): Promise<RequisitionRegister> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/requisitions/register`, params));
    return res.data?.data;
  }

  async getQuickNotes(params?: DineQueryRequest): Promise<PaginatedResponse<DineQuickNote>> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/menu/quick-notes`, params ?? {}));
    return toPaged<DineQuickNote>(res.data);
  }

  async createQuickNote(body: { noteText: string; displayOrder: number }): Promise<DineQuickNote> {
    const res = await post<any>(buildEndpoint(`${BASE}/menu/quick-notes`), body);
    return res.data?.data;
  }

  async updateQuickNote(
    id: ID,
    body: { noteText: string; displayOrder: number; isActive: boolean; rowVersion: number },
  ): Promise<DineQuickNote> {
    const res = await put<any>(buildEndpoint(`${BASE}/menu/quick-notes/${id}`), body);
    return res.data?.data;
  }

  async deleteQuickNote(id: ID, rowVersion: number): Promise<void> {
    await del<any>(buildEndpoint(`${BASE}/menu/quick-notes/${id}?rowVersion=${rowVersion}`));
  }

  // ---------------------------------------------------------------- tax

  /** Rate codes of the outlet's jurisdiction, read from CRS's tax master. */
  async getTaxRates(): Promise<DineTaxRate[]> {
    const res = await get<any>(buildEndpoint(`${BASE}/menu/tax/rates`));
    return res.data?.data ?? [];
  }

  async getItemTax(id: ID): Promise<DineItemTaxHistory> {
    const res = await get<any>(buildEndpoint(`${BASE}/menu/items/${id}/tax`));
    return res.data?.data ?? { history: [] };
  }

  // ---------------------------------------------------------------- bills (cycle 4)

  /**
   * The queue at one counter (DO-313): the one named, else the caller's shift's counter;
   * `all` for every counter.
   */
  /** Tables whose waiter asked for the bill. includeServed adds served orders that have not asked (the New bill picker). */
  async getAwaitingBills(counter?: { billingPointId?: string; all?: boolean }, includeServed = false): Promise<DineAwaitingBill[]> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/bills/awaiting`, { ...(counter ?? {}), ...(includeServed ? { includeServed: true } : {}) }));
    return res.data?.data ?? [];
  }

  async getBills(status: 'open' | 'settled' | 'void' | 'all' = 'open', date?: string,
    counter?: { billingPointId?: string; all?: boolean }): Promise<DineBillSummary[]> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/bills`, { status, date, ...(counter ?? {}) }));
    return res.data?.data ?? [];
  }

  /** A manager moves an unpaid bill to another counter, with a reason (BR-067). */
  async moveBill(id: ID, billingPointId: string, reason: string): Promise<void> {
    await post<any>(buildEndpoint(`${BASE}/bills/${id}/move`), { billingPointId, reason });
  }

  // ------------------------------------------------- billing points (DO-313)

  async getBillingPoints(includeInactive = false): Promise<DineBillingPoint[]> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/billing-points`, { includeInactive }));
    return res.data?.data ?? [];
  }

  async createBillingPoint(body: SaveDineBillingPointRequest): Promise<DineBillingPoint> {
    const res = await post<any>(buildEndpoint(`${BASE}/billing-points`), body);
    return res.data?.data;
  }

  async updateBillingPoint(id: ID, body: SaveDineBillingPointRequest & { isActive: boolean; rowVersion: number }): Promise<DineBillingPoint> {
    const res = await put<any>(buildEndpoint(`${BASE}/billing-points/${id}`), body);
    return res.data?.data;
  }

  async deleteBillingPoint(id: ID, rowVersion: number): Promise<void> {
    await del<any>(buildEndpoint(`${BASE}/billing-points/${id}?rowVersion=${rowVersion}`));
  }

  /** Other outlets of the property a counter here could also serve (shared counter, S1). */
  async getServableOutlets(): Promise<DineServableOutlet[]> {
    const res = await get<any>(buildEndpoint(`${BASE}/billing-points/servable-outlets`));
    return res.data?.data ?? [];
  }

  async getBill(id: ID): Promise<DineBillDetail> {
    const res = await get<any>(buildEndpoint(`${BASE}/bills/${id}`));
    return res.data?.data;
  }

  /** Generate from an order. 409 while a line is unsent or a ticket not served. */
  async generateBill(orderId: ID): Promise<DineBillDetail> {
    const res = await post<any>(buildEndpoint(`${BASE}/orders/${orderId}/bill`), {});
    return res.data?.data;
  }

  async applyBillDiscount(id: ID, body: DiscountInput): Promise<DineBillDetail> {
    const res = await post<any>(buildEndpoint(`${BASE}/bills/${id}/discount`), body);
    return res.data?.data;
  }

  async removeBillDiscount(id: ID): Promise<DineBillDetail> {
    const res = await del<any>(buildEndpoint(`${BASE}/bills/${id}/discount`));
    return res.data?.data;
  }

  /** One line to the house, with a reason (DineOps tax T14; the 'comp' action). */
  async compBillLine(id: ID, orderLineId: ID, body: CompInput): Promise<DineBillDetail> {
    const res = await post<any>(buildEndpoint(`${BASE}/bills/${id}/lines/${orderLineId}/comp`), body);
    return res.data?.data;
  }

  async uncompBillLine(id: ID, orderLineId: ID): Promise<DineBillDetail> {
    const res = await del<any>(buildEndpoint(`${BASE}/bills/${id}/lines/${orderLineId}/comp`));
    return res.data?.data;
  }

  /** The whole bill to the house: settled as Complimentary, no receipt. Undone only by void. */
  async complimentaryBill(id: ID, body: CompInput): Promise<DineBillDetail> {
    const res = await post<any>(buildEndpoint(`${BASE}/bills/${id}/complimentary`), body);
    return res.data?.data;
  }

  async setBillServiceCharge(id: ID, on: boolean): Promise<DineBillDetail> {
    const res = on
      ? await post<any>(buildEndpoint(`${BASE}/bills/${id}/service-charge`), {})
      : await del<any>(buildEndpoint(`${BASE}/bills/${id}/service-charge`));
    return res.data?.data;
  }

  async setBillCustomer(id: ID, body: BillCustomerInput): Promise<DineBillDetail> {
    const res = await put<any>(buildEndpoint(`${BASE}/bills/${id}/customer`), body);
    return res.data?.data;
  }

  async addBillPayment(id: ID, body: PaymentInput): Promise<DineBillDetail> {
    const res = await post<any>(buildEndpoint(`${BASE}/bills/${id}/payments`), body);
    return res.data?.data;
  }

  /** Rounds added after generation land on the bill; receipts and discount stay. */
  async refreshBill(id: ID): Promise<DineBillDetail> {
    const res = await post<any>(buildEndpoint(`${BASE}/bills/${id}/refresh`), {});
    return res.data?.data;
  }

  /** Cycle 7: the bill's room, guest and folio, and whether the folio would take the balance now. */
  async checkRoomCharge(id: ID): Promise<RoomChargeCheck> {
    const res = await get<any>(buildEndpoint(`${BASE}/bills/${id}/room-charge`));
    return res.data?.data;
  }

  /** Cycle 7: charge the balance to the in-house guest's folio; the bill settles. */
  async chargeBillToRoom(id: ID): Promise<DineBillDetail> {
    const res = await post<any>(buildEndpoint(`${BASE}/bills/${id}/room-charge`), {});
    return res.data?.data;
  }

  /** Cycle 7: take the room charge back (reason required); the bill reopens for direct collection. */
  async undoRoomCharge(id: ID, reason: string): Promise<DineBillDetail> {
    const res = await del<any>(buildEndpoint(`${BASE}/bills/${id}/room-charge`), { data: { reason } });
    return res.data?.data;
  }

  async voidBill(id: ID, reason: string): Promise<DineBillDetail> {
    const res = await post<any>(buildEndpoint(`${BASE}/bills/${id}/void`), { reason });
    return res.data?.data;
  }

  async getBillPrint(id: ID): Promise<DineBillPrint> {
    const res = await get<any>(buildEndpoint(`${BASE}/bills/${id}/print`));
    return res.data?.data;
  }

  // ---------------------------------------------------------------- the till (cycle 5)

  /** Void + reissue within the business date; the answer is the new bill. */
  async reissueBill(id: ID, body: ReissueInput): Promise<DineBillDetail> {
    const res = await post<any>(buildEndpoint(`${BASE}/bills/${id}/reissue`), body);
    return res.data?.data;
  }

  /** Money back against one receipt; the answer is the bill. */
  async refundPayment(paymentId: ID, body: RefundInput): Promise<DineBillDetail> {
    const res = await post<any>(buildEndpoint(`${BASE}/payments/${paymentId}/refund`), body);
    return res.data?.data;
  }

  /** The caller's open shift, or null. */
  async getCurrentShift(): Promise<DineShift | null> {
    const res = await get<any>(buildEndpoint(`${BASE}/shifts/current`));
    return res.status === 204 ? null : (res.data?.data ?? null);
  }
  async getShifts(date?: string): Promise<DineShift[]> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/shifts`, date ? { date } : {}));
    return res.data?.data ?? [];
  }
  /** Opens a shift at a counter; the counter may be left out when the outlet has one. */
  async openShift(openingFloat: number, billingPointId?: string): Promise<DineShift> {
    const res = await post<any>(buildEndpoint(`${BASE}/shifts/open`), { openingFloat, billingPointId });
    return res.data?.data;
  }
  async closeShift(id: ID, declaredCash: number, note?: string): Promise<DineShift> {
    const res = await post<any>(buildEndpoint(`${BASE}/shifts/${id}/close`), { declaredCash, note });
    return res.data?.data;
  }

  async getCorrections(params: { from?: string; to?: string; status?: 'unreviewed' | 'all' }): Promise<DineCorrection[]> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/corrections`, params));
    return res.data?.data ?? [];
  }
  async reviewCorrection(body: ReviewInput): Promise<DineCorrection> {
    const res = await post<any>(buildEndpoint(`${BASE}/corrections/review`), body);
    return res.data?.data;
  }

  async getDayClose(date?: string): Promise<DineDayClose> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/day-close`, date ? { date } : {}));
    return res.data?.data;
  }
  async closeDay(businessDate: string, notes?: string): Promise<DineDayClose> {
    const res = await post<any>(buildEndpoint(`${BASE}/day-close`), { businessDate, notes });
    return res.data?.data;
  }
  /** Attach a receipt or refund with no shift to the shift that took it (gate NO_SHIFTLESS); returns the day refreshed. */
  async attachShift(kind: 'RECEIPT' | 'REFUND', id: string, shiftId: string, businessDate?: string): Promise<DineDayClose> {
    const res = await post<any>(buildEndpoint(`${BASE}/day-close/attach-shift`), { kind, id, shiftId, businessDate });
    return res.data?.data;
  }
  /** Record one as taken outside a drawer, with the reason: only when no shift of its day exists to attach it to. */
  async recordOutsideDrawer(kind: 'RECEIPT' | 'REFUND', id: string, reason: string, businessDate?: string): Promise<DineDayClose> {
    const res = await post<any>(buildEndpoint(`${BASE}/day-close/outside-drawer`), { kind, id, reason, businessDate });
    return res.data?.data;
  }

  /** The Day Close summary workbook (S2): Excel, or a PDF to print. An open day is marked Provisional. */
  async exportDayClose(date: string, format: 'xlsx' | 'pdf'): Promise<{ blob: Blob; disposition?: string }> {
    const r = await get<Blob>(buildEndpointWithQuery(`${BASE}/day-close/export`, { date, format }), { responseType: 'blob' });
    return { blob: r.data, disposition: r.headers?.['content-disposition'] as string | undefined };
  }

  /** Bill Register (S2): every bill of the period, the working outlet's or every outlet you may use. */
  async getBillRegister(params: { from: string; to: string; allOutlets?: boolean }): Promise<DineBillRegister> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/reports/bill-register`, params));
    return res.data?.data;
  }

  async exportBillRegister(params: { from: string; to: string; allOutlets?: boolean }, format: 'xlsx' | 'pdf'): Promise<{ blob: Blob; disposition?: string }> {
    const r = await get<Blob>(buildEndpointWithQuery(`${BASE}/reports/bill-register/export`, { ...params, format }), { responseType: 'blob' });
    return { blob: r.data, disposition: r.headers?.['content-disposition'] as string | undefined };
  }

  async getComplaints(params: { from?: string; to?: string; status?: 'all' | 'open' }): Promise<DineComplaint[]> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/complaints`, params));
    return res.data?.data ?? [];
  }
  async createComplaint(body: ComplaintInput): Promise<DineComplaint> {
    const res = await post<any>(buildEndpoint(`${BASE}/complaints`), body);
    return res.data?.data;
  }
  async resolveComplaint(id: ID, resolution: string): Promise<DineComplaint> {
    const res = await post<any>(buildEndpoint(`${BASE}/complaints/${id}/resolve`), { resolution });
    return res.data?.data;
  }

  // ---------------------------------------------------------------- reports

  /** Bills between two business dates, grouped and drilled group -> order -> bill. */
  async getSalesReport(params: { from: string; to: string; groupBy?: string; status?: 'settled' | 'all' }): Promise<DineSalesReport> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/reports/sales`, params));
    return res.data?.data;
  }

  async getSalesByCategory(params: { from: string; to: string; level?: 'course' | 'group' | 'item'; outlets?: 'this' | 'all' }): Promise<DineSalesCategoryReport> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/reports/sales/by-category`, params));
    return res.data?.data;
  }
  async getSettlementReport(date?: string): Promise<DineSettlementReport> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/reports/settlement`, date ? { date } : {}));
    return res.data?.data;
  }
  async getWastageReport(params: { from?: string; to?: string }): Promise<DineWastageReport> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/reports/wastage`, params));
    return res.data?.data;
  }
  async getComplaintsReport(params: { from?: string; to?: string }): Promise<DineComplaintsReport> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/reports/complaints`, params));
    return res.data?.data;
  }
  async getKitchenReport(date?: string): Promise<DineKitchenReport> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/reports/kitchen-performance`, date ? { date } : {}));
    return res.data?.data;
  }
  async getDashboard(): Promise<DineDashboard> {
    const res = await get<any>(buildEndpoint(`${BASE}/reports/dashboard`));
    return res.data?.data;
  }
  async getRevenueSummary(date?: string): Promise<DineRevenueSummary> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/reports/revenue-summary`, date ? { date } : {}));
    return res.data?.data;
  }

  // ---------------------------------------------------------------- price lists

  async getPriceLists(): Promise<DinePriceList[]> {
    const res = await get<any>(buildEndpoint(`${BASE}/menu/price-lists`));
    return res.data?.data ?? [];
  }

  /** With its prices. */
  async getPriceList(id: ID): Promise<DinePriceList> {
    const res = await get<any>(buildEndpoint(`${BASE}/menu/price-lists/${id}`));
    return res.data?.data;
  }

  async createPriceList(body: CreatePriceListRequest): Promise<DinePriceList> {
    const res = await post<any>(buildEndpoint(`${BASE}/menu/price-lists`), body);
    return res.data?.data;
  }

  async updatePriceList(id: ID, body: UpdatePriceListRequest): Promise<DinePriceList> {
    const res = await put<any>(buildEndpoint(`${BASE}/menu/price-lists/${id}`), body);
    return res.data?.data;
  }

  /** The whole set. Refused (409) once the version is in force. */
  async savePriceListPrices(id: ID, prices: ItemPriceInput[]): Promise<DinePriceList> {
    const res = await put<any>(buildEndpoint(`${BASE}/menu/price-lists/${id}/prices`), { prices });
    return res.data?.data;
  }

  /** The next effective row; the current one closes the day before. */
  async newPriceListVersion(id: ID, body: NewPriceListVersionRequest): Promise<DinePriceList> {
    const res = await post<any>(buildEndpoint(`${BASE}/menu/price-lists/${id}/versions`), body);
    return res.data?.data;
  }

  /** UPCOMING versions only. */
  async deletePriceList(id: ID, rowVersion: number): Promise<void> {
    await del<any>(buildEndpoint(`${BASE}/menu/price-lists/${id}?rowVersion=${rowVersion}`));
  }

  // ---------------------------------------------------------------- kitchen tickets

  /** The live feed (NEW/ACCEPTED/PREPARING/READY on live orders), READY first then oldest. */
  async getKots(params?: { station?: ID; status?: 'live' | 'all'; mine?: boolean }): Promise<DineKot[]> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/kots`, params ?? {}));
    return res.data?.data ?? [];
  }

  async getKot(id: ID): Promise<DineKot> {
    const res = await get<any>(buildEndpoint(`${BASE}/kots/${id}`));
    return res.data?.data;
  }

  async acceptKot(id: ID): Promise<DineKot> {
    const res = await post<any>(buildEndpoint(`${BASE}/kots/${id}/accept`), {});
    return res.data?.data;
  }

  async readyKot(id: ID): Promise<DineKot> {
    const res = await post<any>(buildEndpoint(`${BASE}/kots/${id}/ready`), {});
    return res.data?.data;
  }

  async kotLineReady(id: ID, lineId: ID): Promise<DineKot> {
    const res = await post<any>(buildEndpoint(`${BASE}/kots/${id}/lines/${lineId}/ready`), {});
    return res.data?.data;
  }

  /** Reason required. The ticket is cancelled; its order lines go back to unsent. */
  async rejectKot(id: ID, reason: string): Promise<DineKot> {
    const res = await post<any>(buildEndpoint(`${BASE}/kots/${id}/reject`), { reason });
    return res.data?.data;
  }

  async getKotPrint(id: ID): Promise<DineKotPrint> {
    const res = await get<any>(buildEndpoint(`${BASE}/kots/${id}/print`));
    return res.data?.data;
  }

  /** The ticket's print jobs through the Print Agent: status per copy and reprint. */
  async getKotPrintStatus(id: ID): Promise<DineKotPrintStatus> {
    const res = await get<any>(buildEndpoint(`${BASE}/kots/${id}/print-status`));
    return res.data?.data;
  }

  /** Prints the ticket again through the agent, marked REPRINT n. A reason is required (BR-PRN-005). */
  async reprintKot(id: ID, reason: string, printerId?: ID): Promise<DineKotPrintStatus> {
    const res = await post<any>(buildEndpoint(`${BASE}/kots/${id}/reprint`), { reason, printerId: printerId || undefined });
    return res.data?.data;
  }

  // ---------------------------------------------------------------- today's special

  async getBoard(): Promise<DineBoard> {
    const res = await get<any>(buildEndpoint(`${BASE}/menu/specials`));
    return res.data?.data;
  }

  async putOnBoard(body: CreateDineSpecialRequest): Promise<DineBoard> {
    const res = await post<any>(buildEndpoint(`${BASE}/menu/specials`), body);
    return res.data?.data;
  }

  /** One run of dates for many dishes; a dish already booked is re-dated, never doubled. */
  async putManyOnBoard(body: BulkDineSpecialRequest): Promise<DineBoard> {
    const res = await post<any>(buildEndpoint(`${BASE}/menu/specials/bulk`), body);
    return res.data?.data;
  }

  async changeBoardRun(id: ID, body: UpdateDineSpecialRequest): Promise<DineBoard> {
    const res = await put<any>(buildEndpoint(`${BASE}/menu/specials/${id}`), body);
    return res.data?.data;
  }

  /** The dish stays on the menu. */
  async takeOffBoard(id: ID, rowVersion: number): Promise<DineBoard> {
    const res = await del<any>(buildEndpoint(`${BASE}/menu/specials/${id}?rowVersion=${rowVersion}`));
    return res.data?.data;
  }

  async takeManyOffBoard(ids: ID[]): Promise<DineBoard> {
    const res = await post<any>(buildEndpoint(`${BASE}/menu/specials/remove`), { ids });
    return res.data?.data;
  }

  // ---------------------------------------------------------------- goes well with

  async getItemPairs(id: ID): Promise<DineItemPairs> {
    const res = await get<any>(buildEndpoint(`${BASE}/menu/items/${id}/pairs`));
    return res.data?.data;
  }

  /** The whole set. Empty puts the dish back to following its menu group. */
  async saveItemPairs(id: ID, pairs: PairInput[]): Promise<DineItemPairs> {
    const res = await put<any>(buildEndpoint(`${BASE}/menu/items/${id}/pairs`), { pairs });
    return res.data?.data;
  }

  async getGroupPairs(id: ID): Promise<DinePair[]> {
    const res = await get<any>(buildEndpoint(`${BASE}/menu/groups/${id}/pairs`));
    return res.data?.data ?? [];
  }

  async saveGroupPairs(id: ID, pairs: PairInput[]): Promise<DinePair[]> {
    const res = await put<any>(buildEndpoint(`${BASE}/menu/groups/${id}/pairs`), { pairs });
    return res.data?.data ?? [];
  }

  // ---------------------------------------------------------------- photos

  async getItemPhotos(id: ID): Promise<DineItemPhoto[]> {
    const res = await get<any>(buildEndpoint(`${BASE}/menu/items/${id}/photos`));
    return res.data?.data ?? [];
  }

  /**
   * One multipart request for the whole batch, field `files` repeated. The
   * API stores the bytes and the rows together, so nothing is half-attached.
   * Returns the dish's full photo list — the cover can move as a side effect.
   */
  async uploadItemPhotos(id: ID, files: File[]): Promise<DineItemPhoto[]> {
    const form = new FormData();
    files.forEach((f) => form.append('files', f, f.name));
    const res = await post<any>(buildEndpoint(`${BASE}/menu/items/${id}/photos`), form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data?.data ?? [];
  }

  async updateItemPhoto(id: ID, photoId: ID, body: UpdateItemPhotoInput): Promise<DineItemPhoto[]> {
    const res = await put<any>(buildEndpoint(`${BASE}/menu/items/${id}/photos/${photoId}`), body);
    return res.data?.data ?? [];
  }

  async deleteItemPhoto(id: ID, photoId: ID, rowVersion: number): Promise<DineItemPhoto[]> {
    const res = await del<any>(buildEndpoint(`${BASE}/menu/items/${id}/photos/${photoId}?rowVersion=${rowVersion}`));
    return res.data?.data ?? [];
  }
}

/*
 * Multi-outlet (plan O9). This browser tab's outlet goes on every DineOps
 * call as X-Outlet-ID; the API checks it against the user's outlets. Per tab
 * (see auth/dineOutlet), so a switch in one tab never retargets another.
 * Registered here rather than in the shared client so the core interceptors
 * stay untouched, and only /dine calls carry it.
 */
/*
 * Shared counter (S1). A counter's Bill Queue lists bills and served orders of
 * every outlet it serves. Each is remembered with its own outlet here, so every
 * call on that bill, order or receipt goes to the bill's outlet (its numbering,
 * tax and outlet access) without the cashier switching the header outlet. The
 * screens call the service exactly as before.
 */
const recordOutlet = new Map<string, string>();

/** Remember the outlet of bills or orders listed in a queue (only those not of the working outlet matter). */
export function rememberRecordOutlets(rows: { id?: string; orderId?: string; dineOutletId?: string | null }[]): void {
  for (const r of rows) {
    if (!r.dineOutletId) continue;
    if (r.id) recordOutlet.set(r.id.toLowerCase(), r.dineOutletId);
    if (r.orderId) recordOutlet.set(r.orderId.toLowerCase(), r.dineOutletId);
  }
}

const RECORD_IN_URL = /\/dine\/(?:bills|orders|payments)\/([0-9a-f-]{36})/i;

axiosClient.interceptors.request.use((config) => {
  const url = config.url ?? '';
  if (!url.includes('/dine')) return config;
  const id = RECORD_IN_URL.exec(url)?.[1]?.toLowerCase();
  const outletId = (id && recordOutlet.get(id)) || getActiveDineOutletId();
  if (outletId) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>)['X-Outlet-ID'] = outletId;
  }
  return config;
});

// A bill read or changed in another outlet: its receipts, refunded by payment id, belong there too.
axiosClient.interceptors.response.use((response) => {
  const id = RECORD_IN_URL.exec(response.config?.url ?? '')?.[1]?.toLowerCase();
  const outletId = id ? recordOutlet.get(id) : undefined;
  const bill = response.data?.data;
  if (outletId && bill && typeof bill === 'object') {
    if (bill.id) recordOutlet.set(String(bill.id).toLowerCase(), outletId);
    if (Array.isArray(bill.payments)) for (const p of bill.payments) if (p?.id) recordOutlet.set(String(p.id).toLowerCase(), outletId);
  }
  return response;
});

export const dineOpsService = new DineOpsService();
export default dineOpsService;
